import {
  MenuDivider,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
} from '@chakra-ui/react';
import { find, isEmpty, uniq } from 'lodash';
import { useEffect, useState } from 'react';
import invariant from 'tiny-invariant';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { CustomElementClass } from '../../domain/geometry/algorithm-map';
import { Model } from '../../gql/graphql';
import { ShapesMap, useViewer } from '../common/ForgeViewer';

type CategoriesCheckBoxesProps = {
  setCategories: (array: string[]) => void;
  selectedCategories: string[];
  models: Pick<Model, 'ifcCategories' | 'revitCategories'>[];
  shapes: ShapesMap;
};

type CategoryFriendly = {
  friendlyName: string;
  modernValues: string[];
};

export const getCategoriesWithFriendlyName = (
  t: TFunction<[string, string], undefined>
) => {
  return [
    {
      friendlyName: t('categories:beams'),
      modernValues: ['IfcBeam'],
    },
    {
      friendlyName: t('categories:columns'),
      modernValues: ['IfcColumn', 'Revit Columns', 'Revit Structural Columns'],
    },
    {
      friendlyName: t('categories:coverings'),
      modernValues: ['IfcCovering', 'Revit Ceilings'],
    },
    {
      friendlyName: t('categories:curtain-walls'),
      modernValues: ['IfcCurtainWall', 'Revit Curtain Walls'],
    },
    {
      friendlyName: t('categories:doors'),
      modernValues: ['IfcDoor', 'Revit Doors'],
    },
    {
      friendlyName: t('categories:members'),
      modernValues: ['IfcMember', 'Revit Structural Framing'],
    },
    {
      friendlyName: t('categories:railings'),
      modernValues: ['IfcRailing', 'Revit Railings'],
    },
    {
      friendlyName: t('categories:ramps'),
      modernValues: ['IfcRamp', 'Revit Ramps'],
    },
    {
      friendlyName: t('categories:spaces'),
      modernValues: ['IfcSpace', 'Revit Spaces'],
    },
    {
      friendlyName: t('categories:roofs'),
      modernValues: ['IfcRoof', 'Revit Roofs'],
    },
    {
      friendlyName: t('categories:slabs'),
      modernValues: ['IfcSlab', 'Revit Floors'],
    },
    {
      friendlyName: t('categories:stairs'),
      modernValues: ['IfcStair', 'Revit Stairs'],
    },
    {
      friendlyName: t('categories:walls'),
      modernValues: ['IfcWall', 'IfcWallStandardCase', 'Revit Walls'],
    },
    {
      friendlyName: t('categories:windows'),
      modernValues: ['IfcWindow', 'Revit Windows'],
    },
    {
      friendlyName: t('categories:flow-controller'),
      modernValues: ['IfcFlowController'],
    },
    {
      friendlyName: t('categories:duct-segments'),
      modernValues: ['IfcDuctSegment'],
    },
    {
      friendlyName: t('categories:electrical-appliances'),
      modernValues: ['IfcElectricAppliance'],
    },
    {
      friendlyName: t('categories:filters'),
      modernValues: ['IfcFilter'],
    },
    {
      friendlyName: t('categories:flow-meters'),
      modernValues: ['IfcFlowMeter'],
    },
    {
      friendlyName: t('categories:pipe-fittings'),
      modernValues: ['IfcPipeFitting'],
    },
    {
      friendlyName: t('categories:pipe-segments'),
      modernValues: ['IfcPipeSegment'],
    },
    {
      friendlyName: t('categories:valves'),
      modernValues: ['IfcValve'],
    },
    {
      friendlyName: t('categories:waste-terminals'),
      modernValues: ['IfcWasteTerminal'],
    },
    {
      friendlyName: t('categories:sanitary-terminals'),
      modernValues: ['IfcSanitaryTerminal'],
    },
    {
      friendlyName: t('categories:proxy'),
      modernValues: ['IfcBuildingElementProxy', 'Revit Generic Models'],
    },
    {
      friendlyName: t('categories:footings'),
      modernValues: ['IfcFooting'],
    },
    {
      friendlyName: t('categories:piles'),
      modernValues: ['IfcPile'],
    },
    {
      friendlyName: t('categories:foundations'),
      modernValues: ['Revit Structural Foundations'],
    },
  ] as CategoryFriendly[];
};

export function possibleCategories(
  models: Pick<Model, 'ifcCategories' | 'revitCategories'>[],
  translate: TFunction<[string, string], undefined>
): CategoryFriendly[] {
  const categoriesInProject = uniq(
    models.flatMap((model) => [
      ...(model?.ifcCategories ?? []),
      ...(model?.revitCategories ?? []),
    ])
  );
  const categoriesInModelWithFriendlyName = getCategoriesWithFriendlyName(
    translate
  ).filter((category) => {
    return categoriesInProject.some((categoryInModel) =>
      category.modernValues.includes(categoryInModel)
    );
  });

  const modernValues = getCategoriesWithFriendlyName(translate).flatMap(
    (category) => category.modernValues
  );

  return categoriesInModelWithFriendlyName
    .concat({
      friendlyName: translate('categories:shapes'),
      modernValues: [CustomElementClass.PolygonShape],
    })
    .concat({
      friendlyName: translate('categories:other'),
      modernValues: categoriesInProject.filter(
        (value) => !modernValues.includes(value)
      ),
    });
}

export default function CategoriesMenuListCheckBoxes({
  setCategories,
  selectedCategories,
  models,
  shapes,
}: CategoriesCheckBoxesProps): React.ReactElement {
  const {
    isAllModelsLoaded,
    loadedModels,
    visibilityManager: { hiddenModelUrns },
  } = useViewer();
  const [eligibleCategories, setEligibleCategories] = useState<Set<string>>(
    new Set()
  );

  const { t: translate } = useTranslation(['categories']);

  const includeShapesCategory =
    Object.entries(shapes).flatMap(([_, v]) => v).length > 0;

  useEffect(() => {
    const findAndSetEligibleCategories = async () => {
      if (isAllModelsLoaded && !isEmpty(loadedModels)) {
        const newEligibleCategories = new Set<string>();

        for (const modelUrn of Object.keys(loadedModels)) {
          if (modelUrn in hiddenModelUrns) {
            continue;
          }
          const model = loadedModels[modelUrn];
          invariant(model, 'model not found');
          const ifcClassDbIds = await model.findProperty('IfcClass');
          const revitCategoryDbIds = await model.findProperty('Category');

          const ifcCategoriesInModel: string[] = await new Promise(
            (resolve) => {
              model.getBulkProperties2(
                ifcClassDbIds,
                {
                  propFilter: ['IfcClass'],
                },
                (propertyResults) => {
                  resolve(
                    propertyResults.map(
                      (result) =>
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        find(result.properties, ['attributeName', 'IfcClass'])!
                          .displayValue as string
                    )
                  );
                }
              );
            }
          );
          const revitCategoriesInModel: string[] = await new Promise(
            (resolve) => {
              model.getBulkProperties2(
                revitCategoryDbIds,
                {
                  propFilter: ['Category'],
                },
                (propertyResults) => {
                  resolve(
                    propertyResults.map(
                      (result) =>
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        find(result.properties, ['attributeName', 'Category'])!
                          .displayValue as string
                    )
                  );
                }
              );
            }
          );
          for (const ifcCategory of ifcCategoriesInModel) {
            newEligibleCategories.add(ifcCategory);
          }
          for (const revitCategory of revitCategoriesInModel) {
            newEligibleCategories.add(revitCategory);
          }
        }
        if (includeShapesCategory) {
          newEligibleCategories.add(CustomElementClass.PolygonShape);
        }
        setEligibleCategories(newEligibleCategories);
      }
    };

    findAndSetEligibleCategories();
  }, [hiddenModelUrns, includeShapesCategory, isAllModelsLoaded, loadedModels]);

  const handleChange = (selectedCategories: string | string[]) => {
    if (Array.isArray(selectedCategories)) {
      setCategories(selectedCategories);
    } else {
      console.error('Selected categories should always be array');
    }
  };

  const handleAllEvent = (allEvent: string | string[]) => {
    //Allevent.length > 0, All checked
    if (allEvent.length > 0) {
      const allCategories = possibleCategories(models, translate).flatMap(
        (category) => category.friendlyName
      );
      setCategories(allCategories);
    } else {
      setCategories([]);
    }
  };

  return (
    <MenuList maxHeight={'80vh'} overflow={'auto'} zIndex={99}>
      <MenuOptionGroup
        onChange={(e: string | string[]) => handleAllEvent(e)}
        type="checkbox"
        value={
          possibleCategories(models, translate).flatMap(
            (category) => category.friendlyName
          ).length === selectedCategories.length
            ? ['all']
            : []
        }
        //Move this state to component^ can deselect all when something is selected
      >
        <MenuItemOption value="all" key={'all'}>
          {translate('categories:all')}
        </MenuItemOption>
      </MenuOptionGroup>
      <MenuDivider />
      <MenuOptionGroup
        onChange={(e: string | string[]) => {
          handleChange(e);
        }}
        type="checkbox"
        value={selectedCategories}
      >
        {possibleCategories(models, translate)
          .filter((category) =>
            category.modernValues.some((modernValue) =>
              eligibleCategories.has(modernValue)
            )
          )
          .sort((a, b) =>
            a.friendlyName === translate('categories:other')
              ? 1
              : a.friendlyName.localeCompare(b.friendlyName)
          )
          .map((category) => {
            return (
              <MenuItemOption
                value={category.friendlyName}
                key={category.friendlyName}
              >
                {category.friendlyName}
              </MenuItemOption>
            );
          })}
      </MenuOptionGroup>
    </MenuList>
  );
}
